import { Box, Grid, Group, Paper, Select, SimpleGrid, Stack, Text, Title } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { PackageIcon, PlusIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { dateFormat } from "../../utils/date"
import { renderName } from "../../utils/render-name"
import { DeliveryActionButton } from "../delivery/action-controls"
import { DotPill, Tone } from "../delivery/atoms"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { usePermission } from "../permission"
import { usePullSheetStatus } from "../supplier-shipping-customer/pull-sheet-status"
import { ListState } from "../ui"
import { DecisionPanel } from "./DecisionPanel"
import { useActivePullSheetsQuery, usePullSheetLinesQuery, useShortagesByPullSheetQuery } from "./hooks"
import { ReportShortageDialog } from "./ReportShortageDialog"
import { ShortageList } from "./ShortageList"

const STATUS_TONE: Record<string, Tone> = { APPROVED: "grey", LOADING: "amber", CONFIRMED: "blue", LOADED: "green" }

function SummaryItem({ label, children }: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <Box>
      <Text fz={14} fw={600} c="gray.8">
        {label}
      </Text>
      <Text fz={14} fw={700} component="div">
        {children}
      </Text>
    </Box>
  )
}

export function PickingShortage() {
  const { t } = useTranslation(["delivery", "common"])
  const { stateOf } = useDeliveryActions()
  const { nameOf } = usePullSheetStatus()
  const ORD_PS_DETAIL = usePermission("ORD_PS_DETAIL")
  const [searchParams, setSearchParams] = useSearchParams()
  const pullSheetId = searchParams.get("ps") ?? ""
  const [reportOpened, { open: openReport, close: closeReport }] = useDisclosure(false)

  const { data: pullSheets, isLoading: listLoading } = useActivePullSheetsQuery()
  const options = pullSheets ?? []
  const selected = options.find((o) => o.id === pullSheetId) ?? null

  const canSee = ORD_PS_DETAIL.hasAccess
  const { data: shortages, isLoading, error, mutate } = useShortagesByPullSheetQuery(canSee ? pullSheetId || null : null)
  const { data: lines } = usePullSheetLinesQuery(canSee ? pullSheetId || null : null)

  const handleSelect = (value: string | null) => setSearchParams(value ? { tab: "SHORTAGE", ps: value } : { tab: "SHORTAGE" })

  const optionsData = options.map((ps) => ({
    value: ps.id,
    label: [ps.refCode, nameOf(ps.pullSheetStatus), ps.warehouse?.name].filter(Boolean).join(" · "),
  }))

  // The report button follows the matrix: permission, then the dispatched date (O136); no sheet picked = reason too.
  const reportState = stateOf("shortageReport", selected ? { pullSheetStatus: selected.pullSheetStatus, dispatchedDate: selected.dispatchedDate } : null)
  const reportDisabled = !selected ? { allowed: false, reason: t("delivery:shortage.pickFirst") } : reportState

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <Box>
          <Title order={3}>{t("delivery:shortage.title")}</Title>
          <Text fz={14} c="gray.8">
            {t("delivery:shortage.subtitle")}
          </Text>
        </Box>
        <DeliveryActionButton state={reportDisabled} leftSection={<PlusIcon size={16} />} onClick={openReport}>
          {t("delivery:shortage.report")}
        </DeliveryActionButton>
      </Group>

      <Select
        placeholder={listLoading ? t("delivery:shortage.loading") : t("delivery:shortage.selectPlaceholder")}
        data={optionsData}
        value={pullSheetId || null}
        onChange={handleSelect}
        searchable
        clearable
        nothingFoundMessage={t("delivery:shortage.nothingFound")}
        disabled={listLoading}
        comboboxProps={{ shadow: "md" }}
        size="md"
      />

      {selected && (
        <Paper withBorder radius="md" p="md">
          <SimpleGrid cols={{ base: 2, md: 5 }}>
            <SummaryItem label={t("delivery:shortage.summary.number")}>{selected.refCode}</SummaryItem>
            <SummaryItem label={t("delivery:shortage.summary.status")}>
              <DotPill tone={STATUS_TONE[selected.pullSheetStatus] ?? "grey"}>{nameOf(selected.pullSheetStatus)}</DotPill>
            </SummaryItem>
            <SummaryItem label={t("delivery:shortage.summary.warehouse")}>{selected.warehouse?.name || "-"}</SummaryItem>
            <SummaryItem label={t("delivery:shortage.summary.loadingDate")}>{dateFormat(selected.loadingDate)}</SummaryItem>
            <SummaryItem label={t("delivery:shortage.summary.loader")}>{selected.staff ? renderName(selected.staff) : "-"}</SummaryItem>
          </SimpleGrid>
        </Paper>
      )}

      {!pullSheetId ? (
        <Paper withBorder radius="md" py={48} ta="center">
          <Box c="gray.7" mb="xs">
            <PackageIcon size={32} />
          </Box>
          <Title order={5}>{t("delivery:shortage.pickTitle")}</Title>
          <Text fz={14} c="gray.8" maw={440} mx="auto" mt={4}>
            {t("delivery:shortage.pickText")}
          </Text>
        </Paper>
      ) : !canSee ? (
        <ListState state="denied" permission="ORD_PS_DETAIL" />
      ) : error ? (
        <ListState state="error" title={t("delivery:shortage.loadError")} onRetry={() => mutate()} />
      ) : (
        <Grid>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <ShortageList rows={shortages ?? []} lines={lines ?? []} loading={isLoading} reportState={reportDisabled} onReport={openReport} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <DecisionPanel pullSheetId={pullSheetId} />
          </Grid.Col>
        </Grid>
      )}

      {pullSheetId && (
        <ReportShortageDialog opened={reportOpened} onClose={closeReport} pullSheetId={pullSheetId} lines={lines ?? []} shortages={shortages ?? []} />
      )}
    </Stack>
  )
}
